// Offline Detection and Read-Only Mode Utilities

/**
 * Get the current offline status
 * @returns {boolean} True if offline
 */
export function getOfflineStatus() {
  return !navigator.onLine;
}

/**
 * Check if the app should be in read-only mode
 * This considers both network status and service worker cache availability
 * @returns {boolean} True if app should be read-only
 */
export function isReadOnlyMode() {
  return getOfflineStatus();
}

/**
 * Check if a specific feature should be disabled in offline mode
 * @param {string} featureType - Type of feature ('create', 'edit', 'delete', 'schedule', 'import')
 * @returns {boolean} True if feature should be disabled
 */
export function isFeatureDisabled(featureType) {
  if (!getOfflineStatus()) {
    return false; // Online - all features enabled
  }

  // All modification features are disabled offline
  const disabledFeatures = ['create', 'edit', 'delete', 'schedule', 'import', 'assign', 'modify'];
  return disabledFeatures.includes(featureType);
}

/**
 * Get appropriate message for disabled feature in offline mode
 * @param {string} featureType - Type of feature that's disabled
 * @returns {string} User-friendly message
 */
export function getOfflineMessage(featureType) {
  const messages = {
    create: 'Cannot create new items while offline. Connect to internet to enable creation.',
    edit: 'Cannot edit items while offline. Connect to internet to enable editing.',
    delete: 'Cannot delete items while offline. Connect to internet to enable deletion.',
    schedule: 'Cannot schedule sessions while offline. Connect to internet to enable scheduling.',
    import: 'Cannot import data while offline. Connect to internet to enable imports.',
    assign: 'Cannot assign users while offline. Connect to internet to enable assignments.',
    modify: 'Cannot modify data while offline. Connect to internet to enable modifications.',
    general: 'This feature is not available offline. Connect to internet to enable all features.',
  };

  return messages[featureType] || messages.general;
}

/**
 * Get CSS classes for disabled offline elements
 * @param {boolean} isDisabled - Whether the element should be disabled
 * @returns {string} CSS classes for disabled state
 */
export function getOfflineDisabledClasses(isDisabled) {
  if (!isDisabled) return '';

  return 'opacity-50 cursor-not-allowed pointer-events-none select-none';
}

/**
 * Wrap a function to prevent execution when offline
 * @param {Function} fn - Function to wrap
 * @param {string} featureType - Type of feature for error message
 * @param {Function} showError - Error notification function
 * @returns {Function} Wrapped function
 */
export function wrapOfflineFunction(fn, featureType = 'general', showError = null) {
  return (...args) => {
    if (isFeatureDisabled(featureType)) {
      const message = getOfflineMessage(featureType);
      if (showError) {
        showError(message);
      } else {
        console.warn('Offline operation blocked:', message);
      }
      return;
    }
    return fn(...args);
  };
}

/**
 * Check if cached data is available for offline viewing
 * @param {string} dataType - Type of data ('sessions', 'availability', 'crews', etc.)
 * @returns {Promise<boolean>} True if cached data is available
 */
export async function isCachedDataAvailable(dataType) {
  if (!('caches' in window)) {
    return false;
  }

  try {
    const cache = await caches.open('rowing-planner-api-v1');
    const keys = await cache.keys();

    // Check if we have cached data for the requested type
    const hasData = keys.some((request) => {
      const url = new URL(request.url);
      return url.pathname.includes(dataType);
    });

    return hasData;
  } catch (error) {
    console.warn('Error checking cached data:', error);
    return false;
  }
}

/**
 * Get offline capabilities status
 * @returns {Promise<Object>} Object with offline capability information
 */
export async function getOfflineCapabilities() {
  const isOffline = getOfflineStatus();

  if (!isOffline) {
    return {
      isOffline: false,
      readOnlyMode: false,
      availableData: [],
      message: 'Online - All features available',
    };
  }

  const dataTypes = ['sessions', 'availability', 'crews', 'users', 'planning'];
  const availableData = [];

  for (const dataType of dataTypes) {
    if (await isCachedDataAvailable(dataType)) {
      availableData.push(dataType);
    }
  }

  return {
    isOffline: true,
    readOnlyMode: true,
    availableData,
    message:
      availableData.length > 0
        ? `Offline mode - Viewing cached data (${availableData.join(', ')})`
        : 'Offline mode - No cached data available',
  };
}
